<template>
  <div :key="remount" class="container">
    <!-- a poster's own page is a viewer: its own compact header (PosterPage.vue) -->
    <CallToActionBanner v-if="!isPosterViewer" />
    <Header v-if="year && !isPosterViewer" :year />
    <Main v-if="year" />
  </div>
</template>

<script setup>
const log = (msg) => console.log(`App: ${msg}`); // eslint-disable-line
import CallToActionBanner from "@/components/CallToAction/Banner";
import Header from "@/components/Header.vue";
import Main from "@/components/Main.vue";
import {useDisplayWidth} from "@/composables/useDisplayWidth";
import {computed, provide, ref, onMounted, nextTick, watch} from "vue";
import {useRoute} from "vue-router";
import {useAuth, useSession, useUser} from "@clerk/vue";
import {useDisplayMode} from "@/composables/useDisplayMode";
import {useStore} from "vuex";
import {useStorage} from "@vueuse/core";
import {clearPrefillEmail} from "@/composables/prefillEmail";
import useFocusHandlers from "@/composables/useFocusHandlers.js";
import {useSelectedYear} from "@/composables/useSelectedYear";
import {useFullMeetingTitle} from "@/composables/useFullMeetingTitle";
import {setBackgroundImage, ensureBackground} from "@/composables/useBackground";
import {usePageMeta} from "@/composables/usePageMeta";
import history from "@/assets/history.json";
import {getPosterAddress} from "@/composables/usePosters.js";
import "normalize.css";

const isClerkEnabled = typeof window !== "undefined" && window.__clerkEnabled === true;
const {isSignedIn} = isClerkEnabled ? useAuth() : {isSignedIn: ref(false)};
const {user} = isClerkEnabled ? useUser() : {user: ref(null)};
const {isLoaded} = isClerkEnabled ? useSession() : {isLoaded: ref(true)};
provide("authentication", {user, isLoaded, isSignedIn});
// an email link's address (prefillEmail.js) is used once: gone as soon as anyone is signed in
// (signed in already, or the sign-in verified; Seán, 2026-10-27)
watch(isSignedIn, (signedIn) => signedIn && clearPrefillEmail(), {immediate: true});

const route = useRoute();
const isPosterViewer = computed(() => Boolean(getPosterAddress(route)));
// no banner's space or header's background around a poster's page (global.css)
watch(isPosterViewer, (isOn) => document.body.classList.toggle("isPosterViewer", isOn),
  {immediate: true});
const remount = ref(0);
const isSubscribed = ref(false);
const continent = ref("");

const displayWidth = useDisplayWidth();
const displayMode = useDisplayMode();
const store = useStore();

const [selectedYear, selectedTimeZone] = useSelectedYear(); // when a new selection comes in
const year = ref(selectedYear.value); // initially (and eventually) the same as selected

const fullMeetingTitle = useFullMeetingTitle(selectedYear);

provide("isSubscribed", isSubscribed);
provide("displayWidth", displayWidth);
provide("displayMode", displayMode);
provide("history", history);
provide("store", store);
provide("remount", remount);
useStorage("timeZone");
provide("selectedTimeZone", selectedTimeZone);
provide("selectedYear", selectedYear);
provide("year", year);
provide("fullMeetingTitle", fullMeetingTitle);

const yearAndContinent = () => {
  let newYear = selectedYear.value || history.latestYear;
  if (newYear === year.value) return [year.value, continent.value];
  let newContinent = history.years[newYear];
  newContinent = newContinent.match(/^[^/]*/)[0].toLowerCase();
  log(`[year, continent] = [${newYear}, ${newContinent}]`);
  if (newContinent !== continent.value) {
    log("Updating continent in body class list");
    if (document.body.classList && continent.value) {
      document.body.classList.remove(continent.value);
    }
    document.body.classList.add(newContinent);
  }
  return [newYear, newContinent];
};

const documentTitle = (to) => {
  let defaultName = "Visualizing Biological Data (VIZBI)";
  if (!to.name) return defaultName;
  if (to.name === "AboutYear" && to.params.year) return `VIZBI ${to.params.year}`;
  if (to.name.includes("Video")) return "Videos - VIZBI";
  if (to.name === "ShowcaseEvent") return `Showcase - VIZBI ${to.params.year}`;
  if (to.params.year) return `${to.name} - VIZBI ${to.params.year}`;
  if (!to.name.match(/Home/)) return `${to.name} - VIZBI`;
  return defaultName;
};

useFocusHandlers();
usePageMeta(documentTitle);

onMounted(() => {
  remount.value ? log("re-mounted()") : log("mounted()");
  // Defensive safeguard: after Vue has flushed and the browser has painted,
  // verify the background is applied. If stuck in a broken state, recover.
  nextTick(() => requestAnimationFrame(() => requestAnimationFrame(ensureBackground)));
});

watch(
  [route, selectedYear, remount],
  ([to, thisYear, remounts]) => {
    log(`[to.name, thisYear, remounts] = [${to.name}, ${thisYear}, ${remounts}]`);
    [year.value, continent.value] = yearAndContinent();
    log(`year.value = ${year.value}`);
    setBackgroundImage(year.value);
    if (!to.name) return log("No route name");
  },
  {deep: true, immediate: true}
);
</script>

<style>
body {
  display: inline-block;
  color: var(--text-color);
  text-align: left;
  min-width: 20rem;
  max-width: 56rem;
}
</style>
